import { useState } from "react";
import { useResetPassword } from "ra-supabase-core";
import {
  Form,
  required,
  useLocaleState,
  useNotify,
  useRedirect,
  useTranslate,
} from "ra-core";
import { Layout } from "@/components/supabase/layout";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { Link } from "react-router";
import {
  authEmailEnabled,
  selfPasswordResetEnabled,
} from "@/components/atomic-crm/login/authConfig";
import { requestPasswordReset } from "@/components/gavion/auth/passwordResetApi";

interface FormData {
  email: string;
}

export const ForgotPasswordPage = ({
  emailEnabled = authEmailEnabled,
  selfResetEnabled = selfPasswordResetEnabled,
  requestReset = requestPasswordReset,
}: {
  emailEnabled?: boolean;
  /** GAVION: gavion-password-reset sends the link (wins over emailEnabled). */
  selfResetEnabled?: boolean;
  /** Test seam. */
  requestReset?: (email: string, lang: string) => Promise<"sent" | "error">;
}) => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [locale] = useLocaleState();

  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const [, { mutateAsync: resetPassword }] = useResetPassword({
    onSuccess: () => {
      redirect("/login?passwordRecoveryEmailSent=1");
    },
    onError: () => undefined,
  });

  // GAVION: the same generic answer whether or not the address has an account
  const submitSelfReset = async (values: FormData) => {
    setLoading(true);
    try {
      const outcome = await requestReset(values.email.trim(), locale);
      if (outcome === "sent") setSent(true);
      else notify("gavion.login.forgot_error", { type: "warning" });
    } finally {
      setLoading(false);
    }
  };

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      await resetPassword({
        email: values.email,
      });
    } catch (error: any) {
      // GAVION: an Auth mail outage (5xx/timeout) gets the ask-an-admin path
      if (typeof error?.status === "number" && error.status >= 500) {
        notify("gavion.login.forgot_ask_admin", { type: "warning" });
        return;
      }
      notify(
        typeof error === "string"
          ? error
          : typeof error === "undefined" || !error.message
            ? "ra.auth.sign_in_error"
            : error.message,
        {
          type: "warning",
          messageArgs: {
            _:
              typeof error === "string"
                ? error
                : error && error.message
                  ? error.message
                  : undefined,
          },
        },
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.reset_password.forgot_password", {
            _: "Forgot password?",
          })}
        </h1>
        {selfResetEnabled ? (
          sent ? null : (
            <p>{translate("gavion.login.forgot_details")}</p>
          )
        ) : (
          <p>
            {translate("ra-supabase.reset_password.forgot_password_details", {
              _: "Enter your email to receive a reset password link.",
            })}
          </p>
        )}
      </div>
      {selfResetEnabled ? (
        sent ? (
          <div className="space-y-4 text-center">
            <p role="status">{translate("gavion.login.forgot_sent")}</p>
            <p className="text-sm text-muted-foreground">
              {translate("gavion.login.forgot_fallback")}
            </p>
            <Button asChild variant="outline">
              <Link to="/login">{translate("gavion.login.back_to_login")}</Link>
            </Button>
          </div>
        ) : (
          <Form<FormData>
            className="space-y-8"
            onSubmit={submitSelfReset as SubmitHandler<FieldValues>}
          >
            <TextInput
              source="email"
              type="email"
              label={translate("ra.auth.email", { _: "Email" })}
              autoComplete="email"
              validate={required()}
            />
            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                className="cursor-pointer"
                disabled={loading}
              >
                {translate("gavion.login.forgot_submit")}
              </Button>
              <Button asChild variant="link">
                <Link to="/login">
                  {translate("gavion.login.back_to_login")}
                </Link>
              </Button>
            </div>
          </Form>
        )
      ) : !emailEnabled ? (
        <div className="space-y-6 text-center">
          <p role="status">{translate("gavion.login.forgot_ask_admin")}</p>
          <Button asChild variant="outline">
            <Link to="/login">{translate("gavion.login.back_to_login")}</Link>
          </Button>
        </div>
      ) : (
        <Form<FormData>
          className="space-y-8"
          onSubmit={submit as SubmitHandler<FieldValues>}
        >
          <TextInput
            source="email"
            label={translate("ra.auth.email", {
              _: "Email",
            })}
            autoComplete="email"
            validate={required()}
          />
          <Button type="submit" className="cursor-pointer" disabled={loading}>
            {translate("crm.action.reset_password", {
              _: "Reset password",
            })}
          </Button>
        </Form>
      )}
    </Layout>
  );
};

ForgotPasswordPage.path = "forgot-password";
